import React, { forwardRef, useMemo, useRef, useEffect } from 'react';
import { CaretDown, CaretUp, CaretUpDown, Tray } from '@phosphor-icons/react';
import { cn } from '../../../lib/cn';
import { Icon } from '../../Icon';
import { useViewport } from '../../../hooks/useViewport';
import '../../../patterns/console/tablecards.css';

/**
 * Table, the "own and manage" surface.
 *
 * CSS: `desktop/system.css` `.dsk-table-wrap`, `.dsk-table`,
 * `.dsk-table__thumb`, `.dsk-table__rowactions`, `.is-num`, `.is-muted`,
 * `.is-selected`, plus `.dsk-check`, `.dsk-skeleton` and `.dsk-empty--inline`.
 * Behaviour: none of the numbered reference behaviours; sorting and selection
 * are React state here rather than progressive enhancement.
 *
 * Column-config driven so it can replace `components/UI/GenericTable` one for
 * one. A column is:
 *
 *   {
 *     key: 'price',              // also the accessor, dotted paths supported
 *     header: 'Price',
 *     render: (row, index) => node,  // optional, wins over the accessor
 *     numeric: true,             // right-aligned, tabular-nums (`.is-num`)
 *     muted: true,               // `.is-muted`
 *     sortable: true,
 *     sortKey: 'price',          // defaults to `key`
 *     width: 120,                // layout only, never a themeable value
 *     card: 'status',            // optional card slot below 1024, see cardSlot
 *   }
 *
 * NO DENSITY CONTROL. Every row is the comfortable 48px one. The layer still
 * ships `.dsk-table--compact` and this component used to expose a `density`
 * prop and a per-column `renderCompact` for it, but a 40px row cannot hold a
 * thumbnail over two lines, so compact meant every table carried a SECOND set
 * of cell renderers that dropped content — two tables to keep in step for a
 * setting nobody was asked to make. The class stays in the layer, unused.
 *
 * Doctrine held: NEVER zebra stripe, hairlines only, and the hairlines are the
 * stylesheet's business. Row actions live in `.dsk-table__rowactions`, which is
 * `opacity: 0` until `tr:hover` OR `tr:focus-within`; the trigger inside it is
 * a real focusable button, which is the only reason the focus-within half of
 * that rule ever fires, so a hover reveal is always reachable by keyboard.
 * Numeric columns are right aligned with tabular figures.
 *
 * Known layer issues at time of writing, being fixed upstream in
 * `desktop/system.css` rather than worked around here:
 *   - `tbody tr:hover` and `tr.is-selected` tint from `--bg-secondary`, which
 *     equals `--bg-card` in dark theme, so both are invisible there.
 *   - `.dsk-toolbar` does not wrap inside the `overflow: hidden`
 *     `.dsk-table-wrap`, so a crowded toolbar is clipped at 1024px.
 * Neither is patched here; a hand patch to a vendored file is overwritten on
 * the next upstream sync.
 */

/* A pure reset for the sort control. Nothing themeable is set inline: the
   button inherits every colour, font, size and letter-spacing from
   `.dsk-table th`. The native focus outline is deliberately NOT removed, so a
   sortable header stays visibly focusable without inventing a class. */
const SORT_BUTTON = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  padding: 0,
  margin: 0,
  border: 0,
  background: 'none',
  font: 'inherit',
  color: 'inherit',
  letterSpacing: 'inherit',
  textTransform: 'inherit',
  cursor: 'pointer',
};

/**
 * Which SLOT of the card a column lands in below the handoff.
 *
 * A column can say so itself with `card: 'status' | 'meta' | 'amount' | 'fact'
 * | 'hidden'`, and that always wins. Everything below is what happens for the
 * tables that declare nothing, which is all of them today.
 *
 * The defaults are read off props the columns ALREADY set for the table
 * rendering, so no caller has to be touched for the cards to gain a hierarchy:
 *
 *   index 0            the title. It is the thing being listed.
 *   key `status` or
 *   `*_status`         the status slot, beside the title. Every table in the
 *                      app that has a state column names it exactly this
 *                      (`status`, `order_status`, `shipment_status`), and the
 *                      state of a record is the second thing you look for
 *                      after its name — never the fifth label/value cell.
 *   `numeric: true`    an amount. Money and counts are what the row is worth,
 *                      so they get the one large figure on the card.
 *   `muted: true`      quiet meta under the title. A column is already marked
 *                      muted precisely when it corroborates rather than
 *                      identifies — rarity, condition, a created date, a city.
 *   anything else      a labelled fact row.
 *
 * A column that declares nothing and matches nothing still renders, as a fact.
 * The only things ever dropped are an explicit `card: 'hidden'` and a value
 * that would render as nothing anyway — see `isBlank`. A SECOND column
 * declaring `card: 'title'` is not dropped either; it is demoted to a fact,
 * because only one line can be the name of the thing.
 */
const STATUS_KEY = /(^|_)status$/i;

function cardSlot(column, index) {
  if (column.card) return column.card;
  if (index === 0) return 'title';
  if (STATUS_KEY.test(String(column.key || ''))) return 'status';
  if (column.numeric) return 'amount';
  if (column.muted) return 'meta';
  return 'fact';
}

/* A slot that would render nothing costs a line, a separator dot or a whole
   metric column if it is kept. `is_staff` in the admin users table returns
   `null` for most rows, which used to leave a labelled cell with a blank value
   under it. A literal `false` counts as blank for the same reason and not as a
   value being dropped: React renders `false` as nothing, so a raw boolean
   accessor produces exactly that empty labelled cell. */
function isBlank(value) {
  return value === undefined || value === null || value === '' || value === false;
}

function getNestedValue(row, key) {
  if (!key) return undefined;
  return String(key)
    .split('.')
    .reduce((acc, part) => (acc == null ? acc : acc[part]), row);
}

function resolveRowKey(rowKey, row, index) {
  if (typeof rowKey === 'function') return rowKey(row, index);
  if (typeof rowKey === 'string') {
    const value = getNestedValue(row, rowKey);
    return value == null ? index : value;
  }
  return index;
}

/** Bulk-select header box. Indeterminate is a DOM property, not an attribute. */
function SelectAllBox({ checked, indeterminate, onChange, label }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = Boolean(indeterminate);
  }, [indeterminate]);

  return (
    <input
      ref={ref}
      type="checkbox"
      className="dsk-check"
      checked={checked}
      onChange={onChange}
      aria-label={label}
    />
  );
}

export const Table = forwardRef(function Table(
  {
    columns = [],
    data = [],
    rowKey = 'id',
    loading = false,
    skeletonRows = 6,
    selectable = false,
    selectedKeys = [],
    onSelectionChange,
    selectAllLabel = 'Select all rows',
    selectRowLabel = (row, index) => `Select row ${index + 1}`,
    sort = null,
    onSortChange,
    rowActions,
    rowActionsLabel = 'Row actions',
    onRowClick,
    toolbar,
    pagination,
    wrap = true,
    caption,
    emptyState,
    emptyTitle = 'Nothing here yet',
    emptyText,
    className,
    ...rest
  },
  ref,
) {
  const { isDesktop } = useViewport();
  const selected = useMemo(() => new Set(selectedKeys), [selectedKeys]);

  const rowKeys = useMemo(
    () => data.map((row, index) => resolveRowKey(rowKey, row, index)),
    [data, rowKey],
  );

  const allSelected = rowKeys.length > 0 && rowKeys.every((key) => selected.has(key));
  const someSelected = rowKeys.some((key) => selected.has(key));

  const columnCount = columns.length + (selectable ? 1 : 0) + (rowActions ? 1 : 0);

  const toggleAll = () => {
    if (!onSelectionChange) return;
    onSelectionChange(allSelected ? [] : rowKeys);
  };

  const toggleRow = (key) => {
    if (!onSelectionChange) return;
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onSelectionChange(Array.from(next));
  };

  const requestSort = (column) => {
    if (!onSortChange) return;
    const key = column.sortKey || column.key;
    const isSorted = sort && sort.key === key;
    const direction = isSorted && sort.direction === 'asc' ? 'desc' : 'asc';
    onSortChange({ key, direction });
  };

  const renderHeaderCell = (column) => {
    const key = column.sortKey || column.key;
    const isSorted = Boolean(sort && sort.key === key);
    const ascending = isSorted && sort.direction === 'asc';

    let ariaSort;
    if (column.sortable) ariaSort = isSorted ? (ascending ? 'ascending' : 'descending') : 'none';

    return (
      <th
        key={column.key}
        scope="col"
        className={cn(column.numeric && 'is-num', column.headerClassName)}
        style={column.width ? { width: column.width } : undefined}
        aria-sort={ariaSort}
      >
        {column.sortable && onSortChange ? (
          <button type="button" style={SORT_BUTTON} onClick={() => requestSort(column)}>
            {column.header}
            <Icon
              as={isSorted ? (ascending ? CaretUp : CaretDown) : CaretUpDown}
              size="sm"
              active={isSorted}
            />
          </button>
        ) : (
          column.header
        )}
      </th>
    );
  };

  const renderSkeletonRows = () =>
    Array.from({ length: skeletonRows }, (unusedValue, rowIndex) => (
      <tr key={`skeleton-${rowIndex}`}>
        {selectable && (
          <td>
            <span className="dsk-skeleton dsk-skeleton--text" style={{ width: 18 }} />
          </td>
        )}
        {columns.map((column) => (
          <td key={column.key} className={cn(column.numeric && 'is-num')}>
            {/* Widths are the caller's business per the skeleton's own CSS
                comment, so they are layout-only inline values. */}
            <span
              className="dsk-skeleton dsk-skeleton--text"
              style={{ width: column.numeric ? '48%' : '70%' }}
            />
          </td>
        ))}
        {rowActions && (
          <td className="is-num">
            <span className="dsk-skeleton dsk-skeleton--text" style={{ width: 24 }} />
          </td>
        )}
      </tr>
    ));

  const renderEmptyRow = () => (
    <tr>
      <td colSpan={columnCount}>
        {emptyState || (
          <div className="dsk-empty dsk-empty--inline">
            <span className="dsk-empty__icon">
              <Icon as={Tray} />
            </span>
            <p className="dsk-empty__title">{emptyTitle}</p>
            {emptyText ? <p className="dsk-empty__text">{emptyText}</p> : null}
          </div>
        )}
      </td>
    </tr>
  );

  const renderRow = (row, index) => {
    const key = rowKeys[index];
    const isSelected = selected.has(key);

    // A clickable row must also be operable from the keyboard, so it becomes a
    // real tab stop with Enter and Space wired. Prefer putting a link or button
    // in a cell where you can; this is the fallback for whole-row navigation.
    const interactive = Boolean(onRowClick);

    /* A CLICK ON A CONTROL INSIDE THE ROW IS NOT A CLICK ON THE ROW. Without
       this, pressing "Add to cart" or a quantity stepper in a clickable row
       fired the button AND navigated away from the page the button had just
       acted on — the row's handler catching the same event on the way up. Every
       table that pairs `onRowClick` with `rowActions` had this latent, which is
       most of the admin console.

       `closest` is the test rather than `event.target.tagName`, because the
       press usually lands on a glyph or a label INSIDE the control, not on the
       control itself. `currentTarget` bounds the search to this row. */
    const fromControl = (event) =>
      Boolean(
        event.target.closest &&
          event.target.closest('a, button, input, select, textarea, [role="button"]') &&
          event.currentTarget.contains(
            event.target.closest('a, button, input, select, textarea, [role="button"]'),
          ),
      );

    const onKeyDown = interactive
      ? (event) => {
          if (fromControl(event)) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onRowClick(row, index);
          }
        }
      : undefined;

    return (
      <tr
        key={key}
        className={cn(isSelected && 'is-selected')}
        aria-selected={selectable ? isSelected : undefined}
        tabIndex={interactive ? 0 : undefined}
        onClick={
          interactive
            ? (event) => {
                if (fromControl(event)) return;
                onRowClick(row, index);
              }
            : undefined
        }
        onKeyDown={onKeyDown}
      >
        {selectable && (
          <td>
            <input
              type="checkbox"
              className="dsk-check"
              checked={isSelected}
              onChange={() => toggleRow(key)}
              onClick={(event) => event.stopPropagation()}
              aria-label={selectRowLabel(row, index)}
            />
          </td>
        )}
        {columns.map((column) => {
          const renderer = column.render;
          return (
            <td
              key={column.key}
              className={cn(
                column.numeric && 'is-num',
                column.muted && 'is-muted',
                column.className,
              )}
            >
              {renderer ? renderer(row, index) : getNestedValue(row, column.key)}
            </td>
          );
        })}
        {rowActions && (
          <td className="is-num">
            <span className="dsk-table__rowactions">{rowActions(row, index)}</span>
          </td>
        )}
      </tr>
    );
  };

  /**
   * The same rows as CARDS, below the handoff.
   *
   * A table is a comparison instrument: it works because every row's third
   * value is in the same place as every other row's third value, and that
   * property needs width. At 390px there is no width, so the table did the
   * only thing left and rendered 530px wide inside the phone — clipped by
   * `.dsk-table-wrap`'s `overflow: hidden`, which means columns were not
   * merely cramped, they were GONE, with no scrollbar to say so.
   *
   * WHY THIS IS NOT A FACT GRID ANY MORE
   * ------------------------------------
   * The first version of this kept the record honestly and read badly: the
   * lead column became a title and EVERY other column became a label/value
   * cell in a rigid two-column grid. A seven-column listings row therefore
   * produced six cells of identical size and identical weight, so the price,
   * the condition and the created date all shouted at exactly the same volume
   * and the thing came out looking like the table it was trying to stop being.
   * Junaid's words: it reads as a grid, not as a card.
   *
   * A card is not a table row with the columns stacked. It has an ORDER OF
   * IMPORTANCE, and for a record in a marketplace ledger that order is always
   * the same four questions: what is it, what state is it in, what is it
   * worth, and everything else. So the columns are sorted into four slots by
   * `cardSlot` above, each slot gets a different treatment, and only the
   * leftovers keep the label/value form.
   *
   * Nothing is dropped. Both the metrics and the leftovers are real `<dl>`s —
   * a run of name/value pairs about one subject is exactly what a definition
   * list is for — and the two slots that hide their label visually (meta, and
   * status while a card carries only one) carry it in a `.dsk-sr-only` prefix
   * instead, so a screen reader still hears "Shipment: Not booked yet" rather
   * than a loose pill.
   */
  const renderCard = (row, index) => {
    const key = rowKeys[index];
    const isSelected = selected.has(key);
    const interactive = Boolean(onRowClick);

    const cell = (column) =>
      column.render ? column.render(row, index) : getNestedValue(row, column.key);

    // One pass, so a column can only ever land in one slot and the order
    // inside a slot is the order the caller declared its columns in.
    const slots = { title: null, status: [], meta: [], amount: [], fact: [] };
    columns.forEach((column, columnIndex) => {
      const slot = cardSlot(column, columnIndex);
      if (slot === 'hidden') return;
      const value = cell(column);
      if (slot === 'title' && slots.title === null) {
        slots.title = value;
        return;
      }
      if (isBlank(value)) return;
      // Only the FIRST title wins the headline — a second `card: 'title'` used
      // to replace the lead column's cell, then was made to lose its value
      // altogether, which drops a real column off the card. It falls through to
      // a labelled fact instead, so nothing declared is ever unreadable.
      (slots[slot === 'title' ? 'fact' : slot] || slots.fact).push({ column, value });
    });

    const statusLabelled = slots.status.length > 1;

    return (
      /* NOT `role="button"`, exactly as the desktop `<tr>` above takes a
         tabIndex and no role. A container that CARRIES its own controls cannot
         be one — same conclusion as `MenuList.jsx`: this card holds a real
         checkbox and up to three action buttons, and declaring it a button both
         nests interactive content inside a button and strips the `<li>`'s
         listitem role, leaving the `<ul>` a list of nothing. */
      <li
        key={key}
        className={cn('dsa-tablecard', isSelected && 'is-selected')}
        onClick={interactive ? () => onRowClick(row, index) : undefined}
        onKeyDown={
          interactive
            ? (event) => {
                /* Keys from the controls INSIDE the card are not the card's.
                   `__foot` stops click but not keydown, so Space on the
                   selection checkbox was preventDefault'd — the box never
                   toggled and the row opened instead — and Enter on an action
                   button fired the action AND opened the drawer behind it. My
                   listings ships selectable + onRowClick + rowActions at once,
                   so it hit both. */
                if (event.target !== event.currentTarget) return;
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onRowClick(row, index);
                }
              }
            : undefined
        }
        tabIndex={interactive ? 0 : undefined}
      >
        <div className="dsa-tablecard__head">
          {selectable && (
            <input
              type="checkbox"
              className="dsk-check"
              checked={isSelected}
              onChange={() => toggleRow(key)}
              onClick={(event) => event.stopPropagation()}
              aria-label={selectRowLabel(row, index)}
            />
          )}

          <div className="dsa-tablecard__ident">
            <div className="dsa-tablecard__title">{slots.title}</div>

            {/* The corroborating line, values only. These are the columns
                already marked `muted`, which is the caller saying they are
                context — printing "Rarity" and "Condition" above them costs
                two lines to name two words. */}
            {slots.meta.length ? (
              <p className="dsa-tablecard__meta">
                {slots.meta.map(({ column, value }) => (
                  <span className="dsa-tablecard__meta-item" key={column.key}>
                    <span className="dsk-sr-only">{column.header}: </span>
                    {value}
                  </span>
                ))}
              </p>
            ) : null}
          </div>

          {/* State sits WITH the identity, not below the money. It is the
              second question you ask about a record.

              ONE pill carries its own meaning and needs no visible label. TWO
              do not: both Orders tables declare `order_status` (header
              "Payment") and `shipment_status` (header "Shipment"), so the card
              stacked two marks with nothing on screen saying which was which,
              and the shipment cell is often a bare string ("Not booked yet",
              "Bought in person") rather than a pill. So the header shows above
              each mark as soon as there is more than one to tell apart, and
              stays screen-reader-only when there is not. */}
          {slots.status.length ? (
            <div className="dsa-tablecard__status">
              {slots.status.map(({ column, value }) => (
                <span className="dsa-tablecard__status-item" key={column.key}>
                  {statusLabelled ? (
                    <span className="dsa-tablecard__status-label">{column.header}</span>
                  ) : (
                    <span className="dsk-sr-only">{column.header}: </span>
                  )}
                  {value}
                </span>
              ))}
            </div>
          ) : null}
        </div>

        {/* The figures. Label OVER value and the value set large, because on a
            sales ledger the number is the reason the row exists. Laid out
            across the card so a price and a quantity read as one statement. */}
        {slots.amount.length ? (
          <dl className="dsa-tablecard__metrics">
            {slots.amount.map(({ column, value }) => (
              <div key={column.key}>
                <dt>{column.header}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        ) : null}

        {/* Everything the card has no opinion about, as label-left/value-right
            rows. One per line rather than the old two-up grid: two columns of
            wrapping text is the grid Junaid was objecting to, and a table with
            three columns produced a lonely single cell in a two-track grid. */}
        {slots.fact.length ? (
          <dl className="dsa-tablecard__facts">
            {slots.fact.map(({ column, value }) => (
              <div key={column.key}>
                <dt>{column.header}</dt>
                <dd className={cn(column.numeric && 'is-num', column.muted && 'is-muted')}>
                  {value}
                </dd>
              </div>
            ))}
          </dl>
        ) : null}

        {/* Actions get their OWN line, not a slot beside the title. In a table
            they live in a fixed 56px column; on a card the title is already
            wrapping to two lines and a `flex: 0 0 auto` cluster of two or three
            buttons beside it simply overran the name. A full-width row also
            gives each button a real tap target. */}
        {rowActions ? (
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
          <div className="dsa-tablecard__foot" onClick={(event) => event.stopPropagation()}>
            {rowActions(row, index)}
          </div>
        ) : null}
      </li>
    );
  };

  const cards = (
    <div ref={wrap ? undefined : ref} className={cn('dsa-tablecards', wrap ? null : className)}>
      {loading ? (
        <ul className="dsa-tablecards__list" aria-busy="true">
          {/* The skeleton mirrors the CARD, not the columns: a title, a meta
              line and one metric. Laying out a placeholder per column is what
              the old one did, and it drew a six-row fact list that the loaded
              card then never has — the page jumped every time data landed. */}
          {Array.from({ length: skeletonRows }).map((_, index) => (
            <li className="dsa-tablecard" key={index}>
              <div className="dsa-tablecard__head">
                <div className="dsa-tablecard__ident">
                  <span className="dsk-skeleton dsk-skeleton--text" style={{ width: '60%' }} />
                  <span className="dsk-skeleton dsk-skeleton--text" style={{ width: '35%' }} />
                </div>
              </div>
              <dl className="dsa-tablecard__metrics">
                <div>
                  <dt>
                    <span className="dsk-skeleton dsk-skeleton--text" style={{ width: 48 }} />
                  </dt>
                  <dd>
                    <span className="dsk-skeleton dsk-skeleton--text" style={{ width: 80 }} />
                  </dd>
                </div>
              </dl>
            </li>
          ))}
        </ul>
      ) : data.length === 0 ? (
        emptyState || (
          <div className="dsk-empty dsk-empty--inline">
            <span className="dsk-empty__icon">
              <Icon as={Tray} />
            </span>
            <p className="dsk-empty__title">{emptyTitle}</p>
            {emptyText ? <p className="dsk-empty__text">{emptyText}</p> : null}
          </div>
        )
      ) : (
        <ul className="dsa-tablecards__list">{data.map(renderCard)}</ul>
      )}
    </div>
  );

  const table = (
    <table
      ref={wrap ? undefined : ref}
      className={cn('dsk-table', wrap ? null : className)}
      {...(wrap ? {} : rest)}
    >
      {caption ? <caption className="dsk-sr-only">{caption}</caption> : null}
      <thead>
        <tr>
          {selectable && (
            <th scope="col" style={{ width: 44 }}>
              <SelectAllBox
                checked={allSelected}
                indeterminate={!allSelected && someSelected}
                onChange={toggleAll}
                label={selectAllLabel}
              />
            </th>
          )}
          {columns.map(renderHeaderCell)}
          {rowActions && (
            <th scope="col" className="is-num" style={{ width: 56 }}>
              <span className="dsk-sr-only">{rowActionsLabel}</span>
            </th>
          )}
        </tr>
      </thead>
      <tbody>
        {loading ? renderSkeletonRows() : data.length === 0 ? renderEmptyRow() : data.map(renderRow)}
      </tbody>
    </table>
  );

  /* One tree or the other, never both hidden by CSS: a hidden second copy of
     every row doubles the DOM, puts a whole duplicate set of checkboxes and
     row-action buttons in the tab order, and renders every cell twice. Same
     rule the shells follow at the same 1024 threshold. */
  const body = isDesktop ? table : cards;

  if (!wrap) return body;

  return (
    <div ref={ref} className={cn('dsk-table-wrap', className)} {...rest}>
      {toolbar}
      {body}
      {pagination}
    </div>
  );
});

/**
 * TableThumb, the 40x54 card art stand-in used in the first cell of a listings
 * table. CSS: `.dsk-table__thumb`. Renders an <img> when a src is given so the
 * gradient placeholder is only ever the fallback.
 *
 * It is 54px tall and every row is now the comfortable 48px one, which is what
 * it was drawn against.
 */
export const TableThumb = forwardRef(function TableThumb({ src, alt = '', className, ...rest }, ref) {
  if (src) {
    return (
      <img ref={ref} src={src} alt={alt} className={cn('dsk-table__thumb', className)} {...rest} />
    );
  }
  return <span ref={ref} className={cn('dsk-table__thumb', className)} aria-hidden="true" {...rest} />;
});

export default Table;
